import { AdminRole } from '../../../types/adminRoles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AxiosError } from 'axios';

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role?: AdminRole;
  };
  token: string;
}

// Type for useCurrentUser options
interface CurrentUserOptions {
  enabled?: boolean;
  onError?: (error: AxiosError) => void;
}

const API_URL = '/api';

// Setup axios interceptors for authentication
axios.interceptors.request.use(
  (config) => {
    try {
      const storageData = localStorage.getItem('auth-storage');
      if (storageData) {
        const parsed = JSON.parse(storageData);
        // Zustand persist stores data as { state: { ... }, version: number }
        const token = parsed?.state?.token || null;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('Error reading auth token from localStorage:', error);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Handle unauthorized responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Don't redirect if we're already on the login page
      if (window.location.pathname !== '/login') {
        // Clear auth data and redirect to login
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await axios.post(`${API_URL}/auth/login`, credentials);
    return response.data;
  },

  getCurrentUser: async (): Promise<LoginResponse> => {
    // This will throw if not authenticated
    const response = await axios.get(`${API_URL}/auth/me`);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await axios.post(`${API_URL}/auth/logout`);
    localStorage.removeItem('auth-storage');
  },

  // Check if user is authenticated
  checkAuth: async (): Promise<boolean> => {
    try {
      await axios.get(`${API_URL}/auth/me`);
      return true;
    } catch (error) {
      return false;
    }
  },
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
};

export const useCurrentUser = (options?: CurrentUserOptions) => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCheckAuth = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['checkAuth'],
    queryFn: authApi.checkAuth,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000, // 1 minute
    enabled: options?.enabled !== false, // Default to true unless explicitly disabled
    ...options,
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.clear();
    },
  });
};
